import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  DoCheck,
  HostListener,
  Input,
  NgZone,
  OnInit,
  inject,
} from "@angular/core";
import { CommonModule, Location } from "@angular/common";
import {
  Router,
  NavigationStart,
  ActivatedRoute,
  RouterLink,
  RouterLinkActive,
} from "@angular/router";
import { UserService } from "src/app/service/user.service";
import { ChatService } from "src/app/service/chat.service";
import { DataFilterService } from "src/app/data-filter.service";
import { OnlineUsersService } from "src/app/service/online-users.service";
import { Store } from "@ngrx/store";
import { LoginState } from "src/app/store/counter.state";
import { ReactiveFormsModule, FormsModule } from "@angular/forms";
import { NgClass } from "@angular/common";
import { NavigationEnd } from "@angular/router";
import { selectUnreadCount } from "src/app/store/counter.selectors";
import { setUnreadCount } from "src/app/store/counter.actions";
import { Observable } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { InventoryService } from "src/app/service/inventory.service";
import { InfiniteScrollModule } from "ngx-infinite-scroll";

import { ThemeService } from "src/app/service/theme.service";
import { ChatCacheService } from "src/app/service/chat-cache.service";
import { PwaService } from "src/app/service/pwa.service";
import { SettingsModalComponent } from "src/app/component/settings-modal/settings-modal.component";
import { UpdateButtonComponent } from "src/app/component/update-button/update-button.component";
import { NotificationSoundService } from "src/app/service/notification-sound.service";
import { TasksService } from "src/app/features/tasks/services/tasks.service";
import { TaskComposerService } from "src/app/features/tasks/services/task-composer.service";
import { APP_VERSION, isBetaRoute, whatsNewSeen } from "src/app/constants/release-notes";
import { MUTE_OPTIONS, NotificationMuteService, muteEndText, muteLeftText } from "src/app/service/notification-mute.service";

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.css"],
  imports: [
    RouterLink,
    CommonModule,
    RouterLinkActive,
    ReactiveFormsModule,
    FormsModule,
    NgClass,
    InfiniteScrollModule,
    SettingsModalComponent,
    UpdateButtonComponent
  ]
})
export class HeaderComponent implements OnInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);
  // Tasks (14 Oct 2026): badge = what needs me today; the + opens the one New-task window.
  private readonly destroyRef = inject(DestroyRef);
  private readonly tasksService = inject(TasksService);
  private readonly taskComposer = inject(TaskComposerService);
  readonly taskBadge = this.tasksService.badge;
  isTasksPage: boolean = false;
  private taskBadgeTimer: any;

  /**
   * Triggers change detection safely across both Default and OnPush parent components
   * (e.g. TasksPageComponent which uses ChangeDetectionStrategy.OnPush).
   */
  private refreshView(): void {
    this.ngZone.run(() => {
      this.cdr.markForCheck();
      try {
        if (!(this.cdr as any)?.destroyed) {
          this.cdr.detectChanges();
        }
      } catch {
        // Safe fallback if a change detection cycle is already running or component destroyed
      }
    });
  }
  // What's new + Beta (14 Oct 2026)
  readonly appVersion = APP_VERSION;
  isBetaRoute = false;
  get whatsNewSeen(): boolean {
    return whatsNewSeen();
  }
  openWhatsNew(): void {
    void this.router.navigate(["/whats-new"]);
  }
  newTask(): void {
    this.taskComposer.open();
  }
  // Mute (30 Oct 2026, founder): pause every notification for 10 min … 12 h, on every device
  readonly mute = inject(NotificationMuteService);
  readonly muteOptions = MUTE_OPTIONS;
  muteMenuOpen = false;
  /** When the menu opened: each option's "until" is counted from it. */
  private muteMenuAt = 0;
  toggleMuteMenu(): void {
    this.muteMenuOpen = !this.muteMenuOpen;
    this.muteMenuAt = Date.now();
  }
  muteEndFor(minutes: number): number {
    return this.muteMenuAt + minutes * 60000;
  }
  muteEndText(ms: number): string {
    return muteEndText(ms);
  }
  muteLeftText(): string {
    return muteLeftText(this.mute.mutedUntil());
  }
  async chooseMute(minutes: number): Promise<void> {
    if (await this.mute.set(minutes)) this.muteMenuOpen = false;
  }
  @HostListener("document:click")
  @HostListener("document:keydown.escape")
  closeMuteMenu(): void {
    this.muteMenuOpen = false;
  }
  showOnlineUsers: boolean = false;
  onlineUserData: any[];
  playAccess: boolean = false;
  guestId: any;
  userInfo: any;
  // username: string = "";
  unreadCount$: Observable<number>;
  isMobileView: boolean = false;
  userModules: string[] = [];

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public chatService: ChatService,
    private userService: UserService,
    private dataFilter: DataFilterService,
    private onlineUser: OnlineUsersService,
    private inventoryService: InventoryService,
    private store: Store<{ login: LoginState }>,
    private themeService: ThemeService,
    private pwaService: PwaService,
    private soundService: NotificationSoundService,
    private location: Location
  ) {}

  get isStandalone(): boolean {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true ||
      document.referrer.includes('android-app://');
  }

  get canInstallPWA(): boolean {
    // If app is running as standalone installed PWA, don't show option
    if (this.isStandalone) {
      return false;
    }
    // If app is marked as installed in localStorage, don't show option
    if (localStorage.getItem('pwa_installed') === 'true') {
      return false;
    }
    // Show Install App permanently for web browser users who have not installed
    return true;
  }

  get isDarkMode(): boolean {
    return this.themeService.isDarkMode;
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  // hideHeader = false;
  @Input() activeLink: number = -1;
  @Input() totalUnreadCount: number = 0;

  username = localStorage.getItem("username");

  // Default profile image path for fallback
  readonly defaultProfileImage = "../../assets/default-profile.jpeg";

  @Input() hideBackButton: boolean = false;
  isHomePage: boolean = false;
  isCreateProfilePage: boolean = false;
  isFinancePage: boolean = false;
  isWhatsAppBroadcastPage: boolean = false;

  ngOnInit(): void {
    this.mute.start();
    this.listenUnseenNotificationUpdates();
    const modulesStr = localStorage.getItem("modules") || "";
    this.userModules = modulesStr
      ? modulesStr.split(",").map((m: any) => m.trim().toUpperCase()).filter(Boolean)
      : [];
    // Live updates when an admin changes this person's access (ACCESS-UPDATED socket event)
    this.userService.modules$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((modules) => {
      this.userModules = modules;
      this.refreshView();
    });
    if (!this.chatService.isConnected) {
      this.chatService.initChatConnection();
    }
    this.chatService.connectSocket();
    this.unreadCount$ = this.store.select(selectUnreadCount);

    this.unreadCount$.subscribe(count => {
      console.log('Unread count updated:', count);
      this.refreshView();
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.isMainChatPage = this.router.url.includes("/main-chat");
        this.isTasksPage = this.router.url.startsWith("/tasks");
        this.isBetaRoute = isBetaRoute(this.router.url);
        this.checkHomePage();
        this.updateNotification();
        this.refreshView();
      }
    });
    this.isTasksPage = this.router.url.startsWith("/tasks");
    this.isBetaRoute = isBetaRoute(this.router.url);
    void this.tasksService.refreshBadge();
    clearInterval(this.taskBadgeTimer);
    this.taskBadgeTimer = setInterval(() => {
      void this.tasksService.refreshBadge();
      this.refreshView();
    }, 60000);
    this.destroyRef.onDestroy(() => {
      clearInterval(this.taskBadgeTimer);
    });
    try {
      this.chatService.listen("NOTIFICATION").subscribe((n: any) => {
        if (n?.type === "Tasks") {
          void this.tasksService.refreshBadge();
          if (Array.isArray(this.notificationData)) this.notificationData.unshift(n);
          this.refreshView();
        }
      });
    } catch (e) {
      /* socket not ready yet; the minute timer covers it */
    }

    this.checkHomePage();
    this.isMainChatPage = this.router.url.includes("/main-chat");
    if (!this.isMainChatPage) {
      this.getInitialUnreadCount();
    }

    this.updateNotification();
    this.chatService.notification$.subscribe((data) => {
      console.log("New notification received:", data);
      this.notificationData.unshift(data);
      this.refreshView();
    });
    this.getUserDetails();
    this.checkMobileView();
    window.addEventListener('resize', () => this.checkMobileView());
  }

  checkHomePage(): void {
    const url = this.router.url.split('?')[0];
    this.isHomePage = url === '/home' || url === '/' || url === '';
    this.isCreateProfilePage = url === '/create-profile' || url.includes('create-profile');
    this.isFinancePage = url === '/finance' || url.startsWith('/finance');
    this.isWhatsAppBroadcastPage = url === '/whatsapp-broadcast' || url.includes('whatsapp-broadcast');
  }

  private checkMobileView() {
    this.isMobileView = window.innerWidth < 768;
  }

  /** The header's back arrow: the previous screen when the app has one, else Home. Until 20 Oct 2026 it always went to Archive. */
  goBack(): void {
    const nav = (window.history.state && window.history.state.navigationId) || 0;
    if (nav > 1) this.location.back();
    else void this.router.navigate(['/home']);
  }

  getInitialUnreadCount() {
    const userId = localStorage.getItem("guestID");
    this.chatService.getChats().subscribe(
      (resp: any) => {
        const users = resp.data || [];
        let totalUnreadCount = 0;

        users.forEach((user: any) => {
          // Server count is authoritative so reads on other devices are reflected
          let unreadCount = user.unreadCount || 0;

          if (user.type === "group") {
            let isExited = user.isExited;
            if (user.members) {
              const me = user.members.find((m: any) => String(m.userId) === String(userId));
              if (me && (me.leftGroup || me.isRemoved)) {
                isExited = true;
              }
            }
            if (isExited) unreadCount = 0;
          }

          if (unreadCount > 0) totalUnreadCount += unreadCount;
        });

        this.store.dispatch(setUnreadCount({ unreadCount: totalUnreadCount }));
        this.refreshView();
      },
      (err) => {
        console.error("Failed to fetch initial unread count:", err);
        this.refreshView();
      }
    );
  }

  listenUnseenNotificationUpdates() {
    this.chatService.unseenNotificationUpdates$
      .subscribe((socketData) => {
        console.log("SOCKET DATA:", socketData);

        if (!socketData) return;

        let unseenCount;
        if (Array.isArray(socketData)) {
          unseenCount = socketData[1]?.unseenCount;
        }
        else {
          unseenCount = socketData?.unseenCount;
        }

        if (typeof unseenCount !== 'number') return;

        this.notificationCount = unseenCount;
        this.refreshView();
      });
  }

  userRecentProfileImg: string = localStorage.getItem('cachedProfileImg') || localStorage.getItem('profileImg') || "";
  userRecentName: string = localStorage.getItem('cachedUserName') || localStorage.getItem('username') || "";
  userDepartment: string = localStorage.getItem('cachedDepartment') || localStorage.getItem('department') || "";
  userEmployeeId: string = localStorage.getItem('cachedEmployeeId') || localStorage.getItem('employeeId') || "";
  userEmail: string = localStorage.getItem('cachedEmail') || localStorage.getItem('email') || "";

  getUserDetails() {
    const guestID = localStorage.getItem("guestID");
    this.userService.getUserDetails(guestID).subscribe({
      next: (res: any) => {
        this.userInfo = res.data;
        this.userRecentProfileImg = res.data.profileImg || "";
        this.userRecentName = (res.data.firstName + " " + res.data.lastName).trim();
        this.userDepartment = res.data.department || localStorage.getItem('department') || "";
        this.userEmployeeId = res.data.employeeId || localStorage.getItem('employeeId') || "";
        this.userEmail = res.data.email || localStorage.getItem('email') || "";

        if (this.userDepartment) {
          localStorage.setItem('department', this.userDepartment);
          localStorage.setItem('cachedDepartment', this.userDepartment);
        }
        if (this.userEmployeeId) {
          localStorage.setItem('employeeId', this.userEmployeeId);
          localStorage.setItem('cachedEmployeeId', this.userEmployeeId);
        }
        if (this.userEmail) {
          localStorage.setItem('email', this.userEmail);
          localStorage.setItem('cachedEmail', this.userEmail);
        }

        // Update modules in localStorage
        const modulesData = res?.data?.modules || res?.data?.moduleAccess;
        if (modulesData) {
          const modulesVal = Array.isArray(modulesData) ? modulesData.join(",") : modulesData;
          localStorage.setItem('modules', modulesVal);
          this.userModules = modulesVal
            .split(",")
            .map((m: any) => m.trim().toUpperCase())
            .filter(Boolean);
        }

        const subModulesData = res?.data?.subModules || res?.data?.subModuleAccess;
        if (subModulesData) {
          const subModulesVal = typeof subModulesData === 'object' ? JSON.stringify(subModulesData) : subModulesData;
          localStorage.setItem('subModules', subModulesVal);
        }

        // Cache for instant display on next page load / refresh
        localStorage.setItem('cachedProfileImg', this.userRecentProfileImg);
        localStorage.setItem('cachedUserName', this.userRecentName);
        console.log('Updated user modules from header:', this.userModules);
        this.refreshView();
      },
      error: () => {
        this.refreshView();
      }
    });
  }


  ngAfterViewInit() {
    this.onlineUser.getUsers().subscribe((res) => {
      this.onlineUserData = res.data.filter(
        (user) => user.isActive && this.guestId !== user.userId
      );
      this.refreshView();
    });

    this.guestId = localStorage.getItem("guestID");

    this.dataFilter.disableUser.subscribe((res) => {
      console.log("header play list checking =>", res);
      if (res.data == "playlist-user-login") {
        this.playAccess = true;
        let nav = document.getElementById("navbarMenu");
        if (nav) nav.style.setProperty("display", "none", "important");
        let form = document.getElementById("formDis");
        if (form) form.style.setProperty("display", "none", "important");
        let online = document.getElementById("onlineUser");
        if (online) online.style.setProperty("display", "none", "important");
      }
    });

    let menu = document.getElementById("navbarMenu");
    let links = menu?.querySelectorAll(".nav-link");
    if (links && links[this.activeLink]) {
      links[this.activeLink].classList.add("nav-active");
    }
  }
  hasMoreNotifications = true;
  onScroll() {
    console.log("Scrolled! Current Notification Length:", this.notificationData.length);
    const totalLoaded = this.notificationData.length;
    if (totalLoaded >= this.totalnotification) {
      this.hasMoreNotifications = false;
      this.refreshView();
      return;
    }
    this.currentPageNum++;
    this.getNotification();
  }

  getOnlineUsers() {
    this.showOnlineUsers = !this.showOnlineUsers;
  }
  guestToken: any = "";
  onClickLogout() {
    this.chatService.disconnectSocket();

    const browserUniqueId = localStorage.getItem("browserUniqueId");
    const guestID = localStorage.getItem("guestID");
    const fcmToken = localStorage.getItem("fcm_token");

    if (guestID && fcmToken) {
      this.userService.removeToken({ id: guestID, token: fcmToken }).subscribe({
        next: () => {
          console.log("FCM token removed");
        },
        error: (err) => {
          console.error("Token removal failed:", err);
        },
      });
    }

    if (browserUniqueId) {
      this.userService.logOut(browserUniqueId).subscribe({
        next: () => {
          console.log("Browser logout success");
        },
        error: (err) => {
          console.error("Logout API failed:", err);
        },
      });
    }

    this.soundService.syncToServiceWorker('active_user_id', null);
    const savedThemes = ThemeService.preserveTheme();
    ChatCacheService.clearAllUserCaches();
    localStorage.clear();
    ThemeService.restoreTheme(savedThemes);
    if (browserUniqueId) {
      localStorage.setItem("browserUniqueId", browserUniqueId);
    }

    this.router.navigate(["/login"]);
  }

  isMainChatPage: boolean = false;

  hasModule(moduleName: string): boolean {
    if (!moduleName) return false;
    return this.userModules.some(m => m.toUpperCase() === moduleName.toUpperCase());
  }

  showNotificationPanel: boolean = false;
  currentPageNum: number = 1;
  notificationData: any[] = [];
  totalnotification: number = 0;
  notificationCount: number = 0;
  loadingNotifications: boolean = false;

  toggleNotifications() {
    this.showNotificationPanel = !this.showNotificationPanel;

    if (this.showNotificationPanel) {
      this.currentPageNum = 1;
      this.hasMoreNotifications = true;
      this.getNotification();
    }
    this.refreshView();
  }

  closeNotification() {
    this.showNotificationPanel = false;
    this.refreshView();
  }

  getNotification() {
    this.loadingNotifications = true;
    this.refreshView();

    const option = {
      page: this.currentPageNum,
      limit: "100",
    };

    this.inventoryService.globalNotication(option).subscribe({
      next: (res: any) => {
        const newData = res?.data?.notifications || [];
        this.totalnotification = res?.data?.totalCount || 0;

        if (this.currentPageNum === 1) {
          this.notificationData = newData;
        } else {
          this.notificationData = [...this.notificationData, ...newData];
        }

        if (this.notificationData.length >= this.totalnotification) {
          this.hasMoreNotifications = false;
        }

        console.log("Fetched Notifications:", this.notificationData);

        this.loadingNotifications = false;
        this.refreshView();
      },
      error: (error) => {
        console.error("Error fetching notifications:", error);
        this.loadingNotifications = false;
        this.refreshView();
      }
    });
  }


  installPWA() {
    // Shows the installation guide first, then the browser's own Install dialog where there is one.
    void this.pwaService.installPWA();
  }

  updateNotification() {
    this.inventoryService.getNotificationCount().subscribe({
      next: (res: any) => {
        const count = res?.data?.unseenCount ?? 0;
        this.notificationCount = count;
        this.refreshView();
      },
      error: (err) => {
        console.error("Failed to get notification count:", err);
        this.refreshView();
      }
    });
  }




  formatNotificationDate(dateString: string): string {
    const date = new Date(dateString);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const formattedHours = hours % 12 || 12;
    const ampm = hours >= 12 ? "PM" : "AM";
    const formattedMinutes = minutes < 10 ? "0" + minutes : minutes;
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "long" });
    const year = date.getFullYear();

    return `${formattedHours}:${formattedMinutes} ${ampm} on ${day} ${month}, ${year}`;
  }

  handleNotificationRedirect(notification: any) {
    notification.isClicked = true;
    notification.status = 'seen';
    this.closeNotification();
    this.refreshView();
    const rawUrl = notification.meta?.redirectUrl || '';
    const redirectUrl = decodeURIComponent(rawUrl);

    if (redirectUrl.startsWith('/')) {
      this.router.navigateByUrl(redirectUrl);
      return;
    }
    if (redirectUrl.startsWith('http')) {
      window.location.href = redirectUrl;
      return;
    }

    // The record delete/move request notifications carry ids in meta rather
    // than a redirect URL, so the deep link is built from those.
    const workspaceUrl = this.buildProjectTrackerUrl(notification);
    if (workspaceUrl) {
      this.router.navigateByUrl(workspaceUrl);
      return;
    }

    console.warn('Invalid or missing redirect URL:', redirectUrl);
  }

  private readonly projectTrackerSubTypes = ['Record Deletion Request', 'Record Move Request'];

  /**
   * Builds the link-workspace deep link for a project tracker request
   * notification. Falls back to the approval queue when meta has no folder id
   * to open the record in.
   */
  private buildProjectTrackerUrl(notification: any): string | null {
    if (!this.projectTrackerSubTypes.includes(notification?.subType)) return null;

    const meta = notification.meta || {};
    const folderId = meta.folderId || meta.parentFolderId || '';
    if (!folderId) return '/link-workspace?view=requests';

    const params = new URLSearchParams({ folderId });
    if (meta.rootFolderId && meta.rootFolderId !== folderId) {
      params.set('parentId', meta.rootFolderId);
    }
    if (meta.recordId) {
      params.set('recordId', meta.recordId);
      params.set('tab', 'activity');
    }
    return `/link-workspace?${params.toString()}`;
  }

  hasUnreadNotifications(): boolean {
    return this.notificationData.some(notification => !notification.isClicked && notification.status !== 'seen');
  }

  /**
   * Handle profile image error in notifications.
   * Sets the image source to default profile image when loading fails.
   * @param event - The error event from the img tag
   */
  handleProfileImageError(event: Event): void {
    const imgElement = event.target as HTMLImageElement;
    if (!imgElement.src.includes('default-profile.jpeg')) {
      imgElement.src = this.defaultProfileImage;
    }
  }

  showSettingsModal: boolean = false;

  openSettings(): void {
    this.showSettingsModal = true;
    const dropdown = document.querySelector(".profile-dropdown");
    dropdown?.classList.remove("show");
    dropdown?.querySelector(".dropdown-menu")?.classList.remove("show");
  }

  closeSettings(): void {
    this.showSettingsModal = false;
  }

  markAllAsRead(): void {
    this.chatService.markAsRead().subscribe({
      next: (res: any) => {
        console.log("All notifications marked as read:", res);
        this.notificationData = this.notificationData.map((notification) => {
          return {
            ...notification,
            isClicked: true,
            status: 'seen'
          };
        });
        this.notificationCount = 0;
        this.updateNotification();
        this.refreshView();
      },
      error: (err) => {
        console.error("Failed to mark all notifications as read:", err);
        this.refreshView();
      }
    });
  }

}
